import React, { useState } from 'react';
import { Localizer } from 'services/Localizer';
import { ServerData } from 'services/ServerData';
import Spinner from 'shared/components/Spinner';
import { TextBoxField, DropdownField, CheckBoxField } from 'shared/edit/fields';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import InlineAlert from 'shared/edit/InlineAlert';
import RocForm from 'shared/edit/RocForm';
import { useAxiosFormSubmitter } from 'shared/hooks/useBaseAxiosFormSubmitter';
import AccountUrls from 'shared/util/AccountUrls';
import { DropdownOption } from '../../../../Roc.SharedClientApp/types/fields';
import { RegistrationData } from './types';
import CheckboxFieldWithCallback from 'shared/edit/fields/tapecase/CheckboxFieldWithCallback';
import CheckboxListFieldWithCallback, {
	CheckboxOption,
} from 'shared/edit/fields/tapecase/CheckboxListFieldWithCallback';
import IconWithTooltip from 'shared/components/IconWithTooltip';
import HelpSVG from 'shared/components/svg/HelpSVG';

function getRegistrationData(): RegistrationData {
	return (window as any).__TAPECASE_REGISTRATION_DATA__ ?? {};
}

interface RegisterValues {
	email: string;
	firstName: string;
	lastName: string;
	password: string;
	confirmPassword: string;
	organizationIndustry: string | null;
	company: string;
	phone: string;
	isOptedInForMarketingEmails: boolean;
	isDistributor: boolean;
	distributorIndustries: string[] | null;
	address: string;
	address2: string;
	city: string;
	state: string;
	zipCode: string;
	account3M: string;
	paymentTerms3M: string;
	salesRep3M: string;
}

/**
 * The main component that renders Registation page (TapeCase)
 */
export default function Register() {
	const { industries, states, distributorIndustries } = getRegistrationData();
	const organizationIndustriesOptions = industries.map((o) => ({ text: o.name, value: o.id } as DropdownOption));
	const distributorIndustriesOptions = distributorIndustries.map(
		(o) => ({ text: o.name, value: o.id } as CheckboxOption),
	);

	const [isDistributorRegistration, setIsDistributorRegistration] = useState<boolean>(false);
	const [is3MDistributorRegistration, setIs3MDistributorRegistration] = useState<boolean>(false);
	const [selectedDistributorIndustries, setSelectedDistributorIndustries] = useState<string[]>([]);

	const onOrganizationTypeSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		setIsDistributorRegistration(e.target.checked);
	};

	const on3MDistributorSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		setIs3MDistributorRegistration(e.target.checked);
	};

	// eslint-disable-next-line no-unused-vars
	const usStates = states.filter((s) => s.countryCode.toUpperCase() === 'US');

	const registerSubmitter = useAxiosFormSubmitter<RegisterValues>(
		{
			url: '/ajax/user/register',
		},
		'Register',
	);

	if (registerSubmitter.submitted) {
		if (!ServerData.RequireConfirmEmail) {
			document.location.href = AccountUrls.BaseUrl;
			return null;
		} else {
			return (
				<>
					<h1>{ServerData.RegistrationThankYouTitle}</h1>
					<p data-testid="registration-thank-you-message">{ServerData.RegistrationThankYouMessage}</p>
				</>
			);
		}
	} else {
		return (
			<>
				<p className="roc-divider-text">{Localizer('RequiredFields')}</p>
				<h1>{Localizer('Register')}</h1>
				<p>{Localizer('CreateNewAccount')}</p>
				<RocForm<RegisterValues>
					onSubmit={registerSubmitter.handleSubmit}
					render={({ form, handleSubmit, submitError, submitting }) => {
						return (
							<form className="roc-form roc-register-form" onSubmit={handleSubmit}>
								{submitError ? (
									<InlineAlert title={Localizer('ErrorMessageGenericTitle')} message={submitError} />
								) : null}
								<TextBoxField isRequired label={Localizer('Email')} name="email" />

								{!ServerData.USE_EMAIL_AS_USERNAME ? (
									<TextBoxField isRequired label={Localizer('Username')} name="username" />
								) : null}

								<TextBoxField isRequired={true} label={Localizer('FirstName')} name="firstName" />
								<TextBoxField isRequired={true} label={Localizer('LastName')} name="lastName" />
								<IconWithTooltip
									key="registartion_company"
									tooltipText="If registering as an independent customer, please use full name"
									renderIcon={() => <HelpSVG />}
								/>
								<TextBoxField isRequired={true} label={Localizer('Company')} name="company" />
								<TextBoxField isRequired={true} label={Localizer('Address')} name="address" />
								<TextBoxField isRequired={false} label={Localizer('Address2')} name="address2" />
								<TextBoxField isRequired={true} label={Localizer('City')} name="city" />
								<DropdownField
									label={Localizer('State')}
									name="state"
									isRequired
									options={usStates}
									addSelectOption
								/>
								<TextBoxField isRequired={true} label={Localizer('ZipCode')} name="zipCode" />
								<TextBoxField isRequired={false} type="text" label={Localizer('Phone')} name="phone" />

								<CheckboxFieldWithCallback
									label={Localizer('IsDistributor')}
									name="isDistributor"
									onChange={onOrganizationTypeSelected}
								/>

								{isDistributorRegistration ? (
									<>
										<CheckboxFieldWithCallback
											label={Localizer('Is3MDistributor')}
											name="is3MDistributor"
											onChange={on3MDistributorSelected}
										/>

										{is3MDistributorRegistration ? (
											<>
												<TextBoxField
													isRequired={false}
													label={Localizer('Account3M')}
													name="account3M"
												/>

												<TextBoxField
													isRequired={false}
													label={Localizer('PaymentTerms3M')}
													name="paymentTerms3M"
												/>

												<TextBoxField
													isRequired={false}
													label={Localizer('SalesRep3M')}
													name="salesRep3M"
												/>
											</>
										) : null}

										<CheckboxListFieldWithCallback
											label={Localizer('DistributorIndustries')}
											name="distributorIndustries"
											items={distributorIndustriesOptions}
											onChange={(e) => {
												let distributorIndustries = selectedDistributorIndustries;

												if (e.target.checked) {
													if (!distributorIndustries.includes(e.target.value)) {
														distributorIndustries.push(e.target.value);
													}
												} else {
													distributorIndustries = distributorIndustries.filter(function (
														item,
													) {
														return item !== e.target.value;
													});
												}

												setSelectedDistributorIndustries(distributorIndustries);

												form.change('distributorIndustries', distributorIndustries);
											}}
										/>
									</>
								) : (
									<>
										<DropdownField
											isRequired={true}
											label={Localizer('OrganizationIndustry')}
											name="organizationIndustry"
											options={organizationIndustriesOptions}
										/>
									</>
								)}

								<TextBoxField
									isRequired
									type="password"
									label={Localizer('Password')}
									name="password"
								/>
								<TextBoxField
									isRequired
									type="password"
									label={Localizer('ConfirmPassword')}
									name="confirmPassword"
								/>

								<CheckBoxField
									label={Localizer('OptInForMarketingEmails')}
									name="isOptedInForMarketingEmails"
									initialValue={true}
								/>

								<ReCaptchaDisclaimer />

								<button
									disabled={submitting}
									type="submit"
									color="secondary"
									className="roc-btn"
									data-testid="registration-submit"
								>
									{Localizer('Register')} {submitting ? <Spinner light /> : null}
								</button>
							</form>
						);
					}}
				/>
			</>
		);
	}
}
